import { isSafeRedirect } from './post-auth-redirect'

/**
 * Session expiry: one central place for "the API answered 401 on a signed-in page".
 *
 * The signed-in layout registers a handler while it is mounted. The API client calls
 * `reportUnauthorized` for each 401. The handler runs once per page load, so a page with
 * many parallel queries sends the user to sign in one time, not once per query.
 */

let handler: (() => void) | null = null
let fired = false

// A 401 from these calls means "wrong or missing credential", not "the session ended".
const CREDENTIAL_PATHS = /^\/api\/auth\/(me|login|register|logout|magic-link(\/verify)?|2fa\/complete)(\?|$)/

export function isCredentialPath(path: string): boolean {
  return CREDENTIAL_PATHS.test(path)
}

/** Register the handler. It returns a function that removes it. Each new handler can fire once. */
export function onSessionExpired(fn: () => void): () => void {
  handler = fn
  fired = false
  return () => {
    if (handler === fn) handler = null
  }
}

/** Called by the API client for each 401. Does nothing without a handler (public pages). */
export function reportUnauthorized(path: string): void {
  if (!handler || fired || isCredentialPath(path)) return
  fired = true
  handler()
}

/** `/login?redirect=<current path>`. The page the user was on survives the sign-in. */
export function sessionExpiredLoginUrl(loc: { pathname: string; search: string; hash: string }): string {
  const back = `${loc.pathname}${loc.search}${loc.hash}`
  if (loc.pathname.startsWith('/login') || !isSafeRedirect(back)) return '/login'
  return `/login?redirect=${encodeURIComponent(back)}`
}

/** A hard navigation: the app boots again without the dead session. It is a function so tests can replace it. */
export function hardNavigate(url: string): void {
  window.location.assign(url)
}

/** The retry rule for queries: a 401 never recovers by itself, so do not repeat it. Other errors retry once. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 401) return false
  return failureCount < 1
}
